import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  TextField,
  Tooltip,
  IconButton,
  Grid,
  Alert,
  Menu,
  MenuItem,
  Link,
} from '@mui/material';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import React, { useState } from 'react';
import UnitSensorDataChart from './UnitSensorDataChart';

const FlowMoniVolumeCalibrationModule = ({
  sensor,
  unitData,
  isLoading,
  renderChart,
  handlePatchSensor,
  handleSupportClick,
}) => {
  const [volumeConstant, setVolumeConstant] = useState(
    sensor.sensorObject.VolumeConstant || null,
  );
  const [showWarning, setShowWarning] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);

  const renderChipOrValue = (current, target) => {
    if (current === null) return <Typography variant="body1">-</Typography>;
    if (target !== null && target !== current) {
      return (
        <Chip
          label="Change in progress"
          icon={<CircularProgress size={16} />}
          color="primary"
          size="small"
        />
      );
    }
    return <Typography variant="body1">{current}</Typography>;
  };

  const openMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };

  const handleOptionSelect = (option) => {
    setSelectedOption(option);
    closeMenu();
  };

  const handleVolumeConstantChange = (event) => {
    const newValue = event.target.value;
    setVolumeConstant(newValue);

    if (
      sensor.sensorObject.VolumeConstant &&
      newValue !== sensor.sensorObject.VolumeConstant
    ) {
      setShowWarning(true);
    } else {
      setShowWarning(false);
    }
  };

  const handleSave = () => {
    handlePatchSensor({ VolumeConstant: parseFloat(volumeConstant) });
    setShowWarning(false);
    setSelectedOption(null);
  };

  const handleCancel = () => {
    const correctionCoefficient = sensor.sensorObject.VolumeConstant; // Use existing VolumeConstant as correction
    handlePatchSensor({ VolumeConstant: correctionCoefficient });
    setShowWarning(false);
    setSelectedOption(null);
  };

  const handleLocalReset = () => {
    setVolumeConstant(sensor.sensorObject.VolumeConstant || null);
    setShowWarning(false);
    setSelectedOption(null);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="body1"
              fontWeight="bold"
              fontSize="0.9rem"
              color="text.secondary"
            >
              Calibration Factor
            </Typography>
            <Tooltip title="Update the calibration factor either by manually typing in a known constant or by performing a measured flow test and derive the calibration factor based on the measured and actual flow.">
              <IconButton
                size="small"
                onClick={openMenu}
                sx={{ borderRadius: 1 }}
              >
                <EditRoundedIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Box>
            {selectedOption === 'Manual' ? (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {volumeConstant !== sensor.sensorObject.VolumeConstant && (
                    <>
                      <IconButton
                        size="small"
                        onClick={handleSave}
                        sx={{
                          p: 0.5,
                          color: 'text.secondary',
                          borderRadius: 1,
                        }}
                      >
                        <SaveRoundedIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={handleLocalReset}
                        sx={{
                          p: 0.5,
                          color: 'text.secondary',
                          borderRadius: 1,
                        }}
                      >
                        <CancelRoundedIcon />
                      </IconButton>
                    </>
                  )}
                  <TextField
                    variant="filled"
                    size="small"
                    label="Calibration Factor"
                    type="number"
                    value={volumeConstant || ''}
                    onChange={handleVolumeConstantChange}
                    sx={{ width: 120 }}
                    InputProps={{
                      disableUnderline: true,
                      sx: { borderRadius: 1 },
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
                {showWarning && (
                  <Alert severity="warning" sx={{ fontSize: '0.85rem' }}>
                    There already is a calibration factor in use. Changing this
                    will affect the stored unit data.
                  </Alert>
                )}
              </Grid>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1">
                  {sensor.sensorObject.VolumeConstant ?? '-'}
                </Typography>
              </Box>
            )}
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={closeMenu}
          >
            <MenuItem onClick={() => handleOptionSelect('Manual')}>
              Enter the Calibration Factor manually
            </MenuItem>
            <MenuItem onClick={() => handleOptionSelect('Measured')}>
              Derive Calibration Factor from measurement
            </MenuItem>
          </Menu>
        </Box>
      </Grid>

      {selectedOption === 'Measured' && (
        <Grid item xs={12}>
          <div style={{ padding: 4 }}>
            <Alert severity="info" sx={{ fontSize: '0.85rem' }}>
              In order to derive the calibration factor, first ensure that the
              Flow Sensor is correctly mounted and reports an online status as
              well as a stable flow rate. For this calibration method, a known
              flow rate is required to compare the measured flow rate with the
              actual flow rate. The calibration factor is then calculated as the
              ratio between the measured flow rate and the actual flow rate. For
              more information, please visit{' '}
              <Link
                component="button"
                onClick={handleSupportClick}
                sx={{ fontWeight: 'bold', textDecoration: 'underline' }}
              >
                support center
              </Link>
              .
            </Alert>
            <UnitSensorDataChart
              unitData={unitData}
              isLoadingData={isLoading}
              renderChart={renderChart}
              handlePatchSensor={handlePatchSensor}
            />
          </div>
        </Grid>
      )}
    </Grid>
  );
};

export default FlowMoniVolumeCalibrationModule;
